/**
 * ---------------------------------------------------------------------------------------------------------------------
 * TABS PANEL
 * ---------------------------------------------------------------------------------------------------------------------
 * Drives the "Tabs panel" section.
 *
 * The same panels serve both layouts: on desktop the sidebar buttons switch which panel is shown, on mobile each panel
 * carries its own accordion header. Both paths write the same state, so a panel opened on mobile is still the selected
 * one after a resize to desktop.
 */

(function () {
  'use strict';

  class TabsPanel extends HTMLElement {
    connectedCallback() {
      const allPanels = Array.from(this.querySelectorAll('[data-tp-panel]'));

      /**
       * The panels come from content_for 'blocks', the sidebar from section.blocks, and the two can disagree — a
       * section rendering theme blocks can hand Liquid an empty section.blocks while the panels render fine. When
       * that happens the list is empty, so rebuild it from the panels themselves: each one carries its label in the
       * accordion header and points back at its tab id through aria-labelledby.
       */
      if (this.querySelectorAll('[data-tp-tab]').length === 0) this.buildTabs(allPanels);

      const sidebar = this.querySelector('[data-tp-sidebar]');

      this.tabs = Array.from(this.querySelectorAll('[data-tp-tab]'));
      this.accordions = Array.from(this.querySelectorAll('[data-tp-accordion]'));

      // The sidebar always ships in the markup, so drop it when the section genuinely has no tabs
      if (sidebar) sidebar.hidden = this.tabs.length === 0;

      /**
       * Each tab block renders its own panel, so pair them through aria-controls rather than trusting that the
       * sidebar and the panels come out in the same order. Falls back to document order if a tab has no match.
       */
      this.panels = this.tabs.length
        ? this.tabs.map((tab, index) => {
            const id = tab.getAttribute('aria-controls');
            const panel = id ? this.querySelector(`[data-tp-panel][id="${id}"]`) : null;

            return panel || allPanels[index] || null;
          })
        : allPanels;

      this.panels = this.panels.filter(Boolean);

      if (this.panels.length === 0) return;

      this.abortController = new AbortController();
      const { signal } = this.abortController;

      this.tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => this.select(index), { signal });
        tab.addEventListener('keydown', (event) => this.onKeydown(event, index), { signal });
      });

      this.accordions.forEach((accordion, index) => {
        accordion.addEventListener('click', () => this.toggle(index), { signal });
      });

      this.select(0);
    }

    disconnectedCallback() {
      if (this.abortController) this.abortController.abort();
    }

    /* Draw one sidebar button per rendered panel, mirroring what the section renders in Liquid */
    buildTabs(panels) {
      const list = this.querySelector('[data-tp-list]');

      if (!list || panels.length === 0) return;

      panels.forEach((panel, index) => {
        const accordion = panel.querySelector('[data-tp-accordion]');
        const item = document.createElement('li');
        const button = document.createElement('button');

        item.setAttribute('role', 'presentation');

        button.type = 'button';
        button.className = 'tabs-panel__tab';
        button.setAttribute('role', 'tab');
        button.id = panel.getAttribute('aria-labelledby') || `tp-tab-${index}`;
        button.setAttribute('aria-selected', 'false');
        button.tabIndex = -1;
        button.setAttribute('data-tp-tab', '');
        button.textContent = (accordion ? accordion.textContent : '').trim() || 'Tab';

        if (panel.id) button.setAttribute('aria-controls', panel.id);

        item.appendChild(button);
        list.appendChild(item);
      });
    }

    /* Roving arrow-key navigation across the tab list */
    onKeydown(event, index) {
      const keys = { ArrowDown: 1, ArrowRight: 1, ArrowUp: -1, ArrowLeft: -1 };
      const step = keys[event.key];

      if (!step) return;

      event.preventDefault();

      const next = (index + step + this.tabs.length) % this.tabs.length;

      this.select(next);
      this.tabs[next].focus();
    }

    select(index) {
      this.activeIndex = index;

      this.tabs.forEach((tab, tabIndex) => {
        const isActive = tabIndex === index;

        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
        tab.tabIndex = isActive ? 0 : -1;
      });

      this.panels.forEach((panel, panelIndex) => {
        const isActive = panelIndex === index;

        panel.classList.toggle('is-active', isActive);

        // On mobile only the selected panel stays expanded, matching the accordion in the design
        panel.classList.toggle('is-open', isActive);

        const accordion = panel.querySelector('[data-tp-accordion]');

        if (accordion) accordion.setAttribute('aria-expanded', isActive ? 'true' : 'false');
      });
    }

    toggle(index) {
      const panel = this.panels[index];

      if (!panel) return;

      // Tapping the open accordion closes it; tapping another opens that one instead
      if (panel.classList.contains('is-open')) {
        panel.classList.remove('is-open');

        const accordion = panel.querySelector('[data-tp-accordion]');

        if (accordion) accordion.setAttribute('aria-expanded', 'false');
        return;
      }

      this.select(index);
    }
  }

  if (!window.customElements.get('tabs-panel')) {
    window.customElements.define('tabs-panel', TabsPanel);
  }
})();
